import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import ActionsMenu from "./ActionsMenu";
import Swal from "sweetalert2";

export default function Rules() {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [rows, setRows] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    program_id: "",
    acyear_id: "",
    user_type_id: "",
    issue_type: "",
    issue_limit: "",
    renew_limit: "",
    renew_books_limit: "",
    nodays: "",
    reservation_days: "",
    fine_per_day: "",
    ext_nodays: "",
    ext_fine: "",
    cost_in_times: "",
    delete_status: 0,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  // Fetch user types for Rules
  useEffect(() => {
    if (open && clientId && collegeId) {
      axios
        .get(`${API_BASE_URL}/api/rules/${clientId}/${collegeId}/usertypes`)
        .then((res) => {
          if (res.data.success) setUserTypes(res.data.data);
          else setUserTypes([]);
        })
        .catch(() => setUserTypes([]));
    }
  }, [open, clientId, collegeId, API_BASE_URL]);

  // Fetch rules data (server-side pagination)
  const fetchData = async (page = 0, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/rules/${clientId}/${collegeId}?page=${
          page + 1
        }&pageSize=${pageSize}&search=${encodeURIComponent(search)}`
      );
      if (data.success) {
        setRows((data.data.items || []).map((r) => ({ id: r.rule_id, ...r })));
        setRowCount(data.data.total || 0);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to load data",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to load data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData(page, pageSize, search);
    }
    // eslint-disable-next-line
  }, [clientId, collegeId, page, pageSize, search]);

  // Fetch programs for Rules
  useEffect(() => {
    if (open && clientId && collegeId) {
      axios
        .get(`${API_BASE_URL}/api/rules/${clientId}/${collegeId}/programs`)
        .then((res) => {
          if (res.data.success) setPrograms(res.data.data);
          else setPrograms([]);
        })
        .catch(() => setPrograms([]));
    }
  }, [open, clientId, collegeId, API_BASE_URL]);

  // Fetch academic years for Rules
  useEffect(() => {
    if (open && clientId && collegeId) {
      axios
        .get(`${API_BASE_URL}/api/rules/${clientId}/${collegeId}/acyears`)
        .then((res) => {
          if (res.data.success) setAcademicYears(res.data.data);
          else setAcademicYears([]);
        })
        .catch(() => setAcademicYears([]));
    }
  }, [open, clientId, collegeId, API_BASE_URL]);

  const handleOpen = () => {
    setEditing(null);
    setForm({
      program_id: "",
      acyear_id: "",
      user_type_id: "",
      issue_type: "",
      issue_limit: "",
      renew_limit: "",
      renew_books_limit: "",
      nodays: "",
      reservation_days: "",
      fine_per_day: "",
      ext_nodays: "",
      ext_fine: "",
      cost_in_times: "",
      delete_status: 0,
    });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      program_id: row.program_id,
      acyear_id: row.acyear_id,
      user_type_id: row.user_type_id,
      issue_type: row.issue_type,
      issue_limit: row.issue_limit === 0 ? "" : row.issue_limit,
      renew_limit: row.renew_limit === 0 ? "" : row.renew_limit,
      renew_books_limit: row.renew_books_limit === 0 ? "" : row.renew_books_limit,
      nodays: row.nodays === 0 ? "" : row.nodays,
      reservation_days: row.reservation_days === 0 ? "" : row.reservation_days,
      fine_per_day: row.fine_per_day === 0 ? "" : row.fine_per_day,
      ext_nodays: row.ext_nodays === 0 ? "" : row.ext_nodays,
      ext_fine: row.ext_fine === 0 ? "" : row.ext_fine,
      cost_in_times: row.cost_in_times === 0 ? "" : row.cost_in_times,
      delete_status: row.delete_status,
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure? 🧸",
      text: "This will hide the rule, but you can always add a new one!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it! 💖",
      cancelButtonText: "No, keep it! 🥺",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/rules/${clientId}/${collegeId}/${row.rule_id}`);
      await Swal.fire("Deleted! 🗑️", "Your rule has been deleted (softly)!", "success");
      fetchData();
    } catch (err) {
      await Swal.fire(
        "Oops! 😢",
        err?.response?.data?.message || "Delete failed. Please try again.",
        "error"
      );
    }
  };

  const handleSubmit = async () => {
    // Convert all number fields to numbers before sending
    const payload = {
      ...form,
      issue_limit: form.issue_limit === "" ? 0 : Number(form.issue_limit),
      renew_limit: form.renew_limit === "" ? 0 : Number(form.renew_limit),
      renew_books_limit: form.renew_books_limit === "" ? 0 : Number(form.renew_books_limit),
      nodays: form.nodays === "" ? 0 : Number(form.nodays),
      reservation_days: form.reservation_days === "" ? 0 : Number(form.reservation_days),
      fine_per_day: form.fine_per_day === "" ? 0 : Number(form.fine_per_day),
      ext_nodays: form.ext_nodays === "" ? 0 : Number(form.ext_nodays),
      ext_fine: form.ext_fine === "" ? 0 : Number(form.ext_fine),
      cost_in_times: form.cost_in_times === "" ? 0 : Number(form.cost_in_times),
    };
    try {
      if (editing) {
        await axios.put(`${API_BASE_URL}/api/rules/${clientId}/${collegeId}/${editing.rule_id}`, {
          ...payload,
          updated_by: user?.user_email || "system",
          updated_on: new Date().toISOString(),
        });
        setSnackbar({ open: true, message: "Updated successfully", severity: "success" });
      } else {
        await axios.post(`${API_BASE_URL}/api/rules/${clientId}/${collegeId}`, {
          ...payload,
          college_id: collegeId,
          created_by: user?.user_email || "system",
        });
        setSnackbar({ open: true, message: "Created successfully", severity: "success" });
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.api.getRowIndex(params.id) + 1;
      },
    },
    { field: "program_id", headerName: "Program ID", flex: 1 },
    { field: "acyear_id", headerName: "Academic Year ID", flex: 1 },
    { field: "user_type_id", headerName: "User Type ID", flex: 1 },
    { field: "issue_type", headerName: "Issue Type", flex: 1 },
    { field: "issue_limit", headerName: "Issue Limit", flex: 1 },
    { field: "renew_limit", headerName: "Renew Limit", flex: 1 },
    { field: "renew_books_limit", headerName: "Renew Books Limit", flex: 1 },
    { field: "nodays", headerName: "No. of Days", flex: 1 },
    { field: "reservation_days", headerName: "Reservation Days", flex: 1 },
    { field: "fine_per_day", headerName: "Fine/Day", flex: 1 },
    { field: "ext_nodays", headerName: "Ext. No. of Days", flex: 1 },
    { field: "ext_fine", headerName: "Ext. Fine", flex: 1 },
    { field: "cost_in_times", headerName: "Cost in Times", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleEdit} onDelete={handleDelete} />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Library Rules
          </Typography>
          <TextField
            size="medium"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{ color: "#fff" }}
          >
            Add Rule
          </Button>
        </Toolbar>
        <Box height={400} mt={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            autoHeight
          />
        </Box>
      </Paper>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Rule" : "Add Rule"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {/* Row 1: Three dropdowns */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Program"
                  value={form.program_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      program_id: e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                  select
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true, displayEmpty: true }}
                >
                  <option value="">Select Program</option>
                  {programs.map((p) => (
                    <option key={p.program_id} value={p.program_id}>
                      {p.program_name}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Academic Year"
                  value={form.acyear_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      acyear_id: e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                  select
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true, displayEmpty: true }}
                >
                  <option value="">Select Academic Year</option>
                  {academicYears.map((y) => (
                    <option key={y.acyear_id} value={y.acyear_id}>
                      {y.academic_year}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="User Type"
                  value={form.user_type_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      user_type_id: e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                  select
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true, displayEmpty: true }}
                >
                  <option value="">Select User Type</option>
                  {userTypes.map((u) => (
                    <option key={u.user_type_id} value={u.user_type_id}>
                      {u.user_type_name}
                    </option>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Row 2: Issue Type and 4 number fields */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Issue Type"
                  value={form.issue_type}
                  onChange={(e) => setForm({ ...form, issue_type: e.target.value })}
                  select
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true, displayEmpty: true }}
                >
                  <option value="">Select Issue Type</option>
                  <option value="SINGLE">SINGLE</option>
                  <option value="BULK">BULK</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Issue Limit"
                  type="number"
                  value={form.issue_limit}
                  onChange={(e) => setForm({ ...form, issue_limit: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Renew Limit"
                  type="number"
                  value={form.renew_limit}
                  onChange={(e) => setForm({ ...form, renew_limit: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Renew Books Limit"
                  type="number"
                  value={form.renew_books_limit}
                  onChange={(e) => setForm({ ...form, renew_books_limit: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="No. of Days"
                  type="number"
                  value={form.nodays}
                  onChange={(e) => setForm({ ...form, nodays: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* Row 3: Reservation Days, Fine/Day, Ext. No. of Days, Ext. Fine, Cost in Times */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Reservation Days"
                  type="number"
                  value={form.reservation_days}
                  onChange={(e) => setForm({ ...form, reservation_days: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Fine/Day"
                  type="number"
                  value={form.fine_per_day}
                  onChange={(e) => setForm({ ...form, fine_per_day: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Ext. No. of Days"
                  type="number"
                  value={form.ext_nodays}
                  onChange={(e) => setForm({ ...form, ext_nodays: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Ext. Fine"
                  type="number"
                  value={form.ext_fine}
                  onChange={(e) => setForm({ ...form, ext_fine: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  label="Cost in Times"
                  type="number"
                  value={form.cost_in_times}
                  onChange={(e) => setForm({ ...form, cost_in_times: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ color: "#fff" }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
